<template>
  <div data-html2canvas-ignore class="relative" style="z-index: 99999;" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-gray-700 bg-opacity-40 transition-opacity animate__animated animate__fadeIn" style="--animate-duration: 0.4s"></div>

    <!-- Modal container -->
    <div class="fixed inset-0 z-10 overflow-y-auto max-h-[90vh] my-auto">
      <div class="flex min-h-full items-end justify-center p-4 text-left sm:items-center sm:p-0">
        
        <section class="shadow-2xl bg-white max-w-[42rem] w-full rounded-lg animate__animated animate__zoomIn flex flex-col max-h-[90vh]" style="--animate-duration: 0.2s">
          
          <!-- Modal header -->
          <div class="relative bg-white border-b border-gray-200 flex-shrink-0">
            <div class="relative px-8 py-6 flex justify-between items-center w-full">
              <div class="flex items-center">
                <!-- Support icon -->
                <img class="h-10 w-10 flex-none mr-4" src="https://d3sjyulja6yeep.cloudfront.net/logos/refreps_logo_web.png" alt="">
                <div>
                  <div class="flex items-center mb-1">
                    <h2 class="text-2xl font-bold text-gray-900 mr-2">Support & FAQ</h2>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                      Help Center
                    </span>
                  </div>
                  <p class="text-sm text-gray-600 leading-relaxed">Find answers to common questions and get help with your account</p>
                </div>
              </div>
              
              <!-- Enhanced close button -->
              <button @click="closeModal" class="group relative p-2 text-gray-400 hover:text-gray-600 transition-all duration-200 rounded-xl hover:bg-gray-100">
                <svg class="relative w-6 h-6 transform group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- FAQ Content -->
          <div class="px-6 py-4 overflow-y-auto flex-1">
            
            <!-- FAQ Section 1: Manage Learners -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden">
              <div class="bg-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('manageLearners')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-duotone fa-address-book" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Manage Learners</h3>
                    </div>
                  </div>
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="faqSections.manageLearners.isOpen ? 'rotate-180' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <div v-show="faqSections.manageLearners.isOpen" class="px-4 py-3 bg-white">
                <p class="text-sm text-gray-700 leading-relaxed mb-4">
                  This is where you add your students and assign them courses/POV video packs.
                </p>
                
                <!-- Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/support/solutions/articles/151000167462-manage-learners-overview" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to navigate the manage learners page
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/-IEKXTDUKdo')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FAQ Section 2: Educational Courses -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden">
              <div class="bg-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('educationalCourses')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-duotone fa-books" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Educational Courses</h3>
                    </div>
                  </div>
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="faqSections.educationalCourses.isOpen ? 'rotate-180' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <div v-show="faqSections.educationalCourses.isOpen" class="px-4 pt-3 bg-white mb-3">
                <p class="text-sm text-gray-700 leading-relaxed mb-4">
                  This is where you can view resources, course content, and set quiz settings.
                </p>
                
                <!-- Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/en/support/solutions/articles/151000166912-educational-course-overview" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to manage your educational courses
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/FbMuakik7E0')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FAQ Section 3: Supplemental POV Video Packs -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden">
              <div class="bg-gradient-to-r from-gray-50 to-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('videoPacksAndActivities')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-duotone fa-video-plus" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Supplemental POV Video Packs & Learning Activities</h3>
                    </div>
                  </div>
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="faqSections.videoPacksAndActivities.isOpen ? 'rotate-180' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <div v-show="faqSections.videoPacksAndActivities.isOpen" class="px-4 py-3 bg-white">
                <p class="text-sm text-gray-700 leading-relaxed mb-4">
                  Video packs can be assigned under "Manage Learners". Learning Activities are a great supplemental way to assign additional work.
                </p>
                
                <!-- First Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200 mb-2">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/en/support/solutions/articles/151000166909-supplemental-video-packs" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to manage your supplemental POV video packs
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/JanPtCmc84Y')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Second Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/en/support/solutions/articles/151000204667-learning-activities" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to manage your learning activities
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/Tu2vtLuwb6U')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FAQ Section 4: Reports -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden" v-if="mainStore.role == 'Instructor'">
              <div class="bg-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('reports')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-duotone fa-chart-mixed" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Reports</h3>
                    </div>
                  </div>
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="faqSections.reports.isOpen ? 'rotate-180' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <div v-show="faqSections.reports.isOpen" class="px-4 py-3 bg-white">
                <p class="text-sm text-gray-700 leading-relaxed mb-4">
                  View your students' progress, access the gradebook where quiz scores will be sent, and review course completion history where you can view all students who have completed a course through your organization.
                </p>
                
                <!-- First Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200 mb-2">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/en/support/solutions/articles/151000168685-navigating-and-reading-reports-gradebook-" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        Navigating and Reading Reports (Gradebook)
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/q6Dv9sXov0A')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Second Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200 mb-2">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/support/solutions/articles/151000211979-course-completion-history" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        View your organization's course completion history
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/1S0d7LmBwdU')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Third Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/support/solutions/articles/151000174506-view-leaners-missed-quiz-questions-instructor-view-" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to view learners missed quiz questions
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/8kz-OCbhJ3Y')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FAQ Section 5: Send Notification -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden" v-if="mainStore.role == 'Instructor'">
              <div class="bg-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('sendNotification')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-duotone fa-bell" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Send Notification</h3>
                    </div>
                  </div>
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="faqSections.sendNotification.isOpen ? 'rotate-180' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <div v-show="faqSections.sendNotification.isOpen" class="px-4 py-3 bg-white">
                <p class="text-sm text-gray-700 leading-relaxed mb-4">
                  Need to send a reminder or message to your learners? Here you can do that! Send messages to a specific course or to everyone. 
                  <span class="font-medium">Note: learners cannot reply to the messages sent.</span>
                </p>
                
                <!-- Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/en/support/solutions/articles/151000166918-instructor-notifications" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to send notifications
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/qbQU_qmU2p4')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FAQ Section: Purchase More Seats -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden" v-if="mainStore.role == 'Instructor'">
              <div class="bg-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('purchaseSeats')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-solid fa-loveseat" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Need to Purchase More Seats?</h3>
                    </div>
                  </div>
                  
                  <!-- Dropdown arrow -->
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="{ 'rotate-180': faqSections.purchaseSeats.isOpen }"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              
              <!-- Dropdown content -->
              <div v-if="faqSections.purchaseSeats.isOpen" class="px-4 py-4 bg-white">
                <p class="text-gray-700 text-sm leading-relaxed mb-4">
                  Ready to assign courses to more learners? Purchase additional seats to expand your organization's capacity.
                </p>
                
                <!-- Purchase button -->
                <div class="flex justify-center mb-4">
                  <button @click="mainStore.showAddSeats = true; mainStore.showSupportModal = false" class="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 font-medium text-sm">
                    Purchase More Seats
                  </button>
                </div>
                
                <!-- Solution Article and Video -->
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-3 h-3 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="text-xs font-medium text-gray-900">Solution Article</span>
                      </div>
                      <a href="https://support.refreps.com/en/support/solutions/articles/151000176152-how-refreps-seats-work" target="_blank" class="text-blue-600 hover:text-blue-700 transition-colors text-sm leading-relaxed block">
                        How to purchase additional seats
                      </a>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="relative w-32 h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                        <iframe
                            class="w-full h-full"
                            :src="'https://www.youtube.com/embed/' + mainStore.getYouTubeVideoId('https://youtu.be/eZI1ms21tMg')"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Platform Tutorials -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden" v-if="mainStore.role == 'Instructor'">
              <div class="bg-gray-50 border-b border-gray-200">
                <button 
                  type="button" 
                  class="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100 transition-all duration-200"
                  @click="toggleSection('platformTutorials')"
                >
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-solid fa-circle-play" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">Platform Tutorials</h3>
                    </div>
                  </div>
                  <svg 
                    class="w-4 h-4 text-gray-500 transition-transform duration-200" 
                    :class="faqSections.platformTutorials.isOpen ? 'rotate-180' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <div v-show="faqSections.platformTutorials.isOpen" class="px-4 py-4 bg-white">
                <p class="text-sm text-gray-700 leading-relaxed mb-4">
                  Walk through the instructor platform or the new purchasing display with step-by-step guided tours.
                </p>
                <div class="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    @click="startInstructorPlatformTutorial"
                    class="flex-1 inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2.5 rounded-lg transition-colors duration-200 font-medium text-sm"
                  >
                    <font-awesome-icon icon="fa-solid fa-circle-question" class="w-4 h-4" />
                    Instructor Platform Tutorial
                  </button>
                  <button
                    type="button"
                    :disabled="mainStore.groupWatchActive"
                    :title="mainStore.groupWatchActive ? 'End your active group watch before starting this tutorial.' : ''"
                    @click="startPurchasingDisplayTutorial"
                    class="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-teal-500 text-teal-600 px-4 py-2.5 rounded-lg transition-colors duration-200 font-medium text-sm"
                    :class="mainStore.groupWatchActive
                      ? 'opacity-50 cursor-not-allowed border-gray-300 text-gray-400'
                      : 'hover:bg-teal-50'"
                  >
                    <font-awesome-icon icon="fa-solid fa-cart-shopping" class="w-4 h-4" />
                    Purchasing Portal Tutorial
                  </button>
                </div>
              </div>
            </div>

            <!-- Support Section -->
            <div class="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-gray-50">
              <div class="px-4 py-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div class="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="fa-duotone fa-ticket" class="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">We're Here to Help!</h3>
                      <p class="text-xs text-gray-600">Visit our support center or submit a ticket</p>
                    </div>
                  </div>
                  <div class="flex space-x-2">
                    <a href="https://support.refreps.com" target="_blank" class="bg-teal-500 hover:bg-teal-700 text-white px-3 py-2 rounded-lg transition-colors duration-200 font-medium text-sm">
                      Support Center
                    </a>
                    <a href="https://support.refreps.com/support/tickets/new" target="_blank" class="bg-white border border-teal-500 text-teal-500 hover:bg-teal-50 px-3 py-2 rounded-lg transition-colors duration-200 font-medium text-sm">
                      Submit Ticket
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Modal footer -->
          <div class="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end rounded-b-lg flex-shrink-0">
            <button 
              @click="closeModal"
              class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
            >
              Close
            </button>
          </div>

        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import useMainStore from "@/stores/main";

export default {
  name: 'FAQSupportModal',
  data() {
    return {
      faqSections: {
        manageLearners: {
          isOpen: false
        },
        educationalCourses: {
          isOpen: false
        },
        videoPacksAndActivities: {
          isOpen: false
        },
        reports: {
          isOpen: false
        },
        sendNotification: {
          isOpen: false
        },
        purchaseSeats: {
          isOpen: false
        },
        platformTutorials: {
          isOpen: false
        }
      }
    }
  },
  computed: {
    ...mapStores(useMainStore)
  },
  methods: {
    closeModal() {
      this.mainStore.showSupportModal = false;
    },
    startInstructorPlatformTutorial() {
      this.mainStore.requestInstructorPlatformTutorial = true
      this.closeModal()
    },
    startPurchasingDisplayTutorial() {
      if (this.mainStore.groupWatchActive) return
      this.mainStore.requestPortalPurchasingTutorial = true
      this.closeModal()
    },
    toggleSection(sectionName) {
      // Check if the clicked section is currently open
      const isCurrentlyOpen = this.faqSections[sectionName].isOpen;
      
      // Close all sections first
      Object.keys(this.faqSections).forEach(key => {
        this.faqSections[key].isOpen = false;
      });
      
      // If the clicked section was closed, open it
      if (!isCurrentlyOpen) {
        this.faqSections[sectionName].isOpen = true;
      }
    },
  },
  mounted() {
    // Optional: Auto-open the first section for better UX
    // this.faqSections.manageLearners.isOpen = true;
  }
}
</script>

<style scoped>
/* Optional: Custom styles for enhanced animations */
.rotate-180 {
  transform: rotate(180deg);
}
</style>
